import { Component } from '@theme/component';

/**
 * <bundle-subscribe>
 *
 * 2-card radio toggle (one-time vs subscribe) sitting next to <bundle-pack>.
 * Communicates with <bundle-pack> via two custom events:
 *   - bundlePack:totalChanged (in)  — refresh card prices on bundle update
 *   - bundleSubscribe:change  (out) — let bundle-pack know the current mode
 *
 * The bundle-pack handles the actual /cart/add.js POST and reads
 * `card.dataset.sellingPlanId` for each selected variant when subscribe mode
 * is active.
 *
 * @typedef {object} BundleSubscribeRefs
 * @property {HTMLInputElement} [oneTimeRadio]
 * @property {HTMLInputElement} [subscribeRadio]
 * @property {HTMLElement} [oneTimeCard]
 * @property {HTMLElement} [subscribeCard]
 * @property {HTMLElement} [oneTimeFinal]
 * @property {HTMLElement} [oneTimeOriginal]
 * @property {HTMLElement} [subscribeFinal]
 * @property {HTMLElement} [subscribeOriginal]
 * @property {HTMLElement} [benefits]
 * @property {HTMLSelectElement} [frequencySelect]
 *
 * @extends Component<BundleSubscribeRefs>
 */
class BundleSubscribe extends Component {
  #abortController = new AbortController();
  #discountPercentage = 0;
  /** Current bundle subtotal in cents (no discount applied). */
  #bundleSubtotal = 0;
  /** Current bundle total after tier discount, in cents. */
  #bundleDiscounted = 0;
  /** Money format string (Liquid-injected, e.g. "{{amount_with_comma_separator}} €"). */
  #moneyFormat = '';

  connectedCallback() {
    super.connectedCallback();

    this.#discountPercentage = parseFloat(this.dataset.discountPercentage || '0') || 0;
    this.#moneyFormat = this.dataset.moneyFormat || '{{amount}} €';

    const { signal } = this.#abortController;

    // Listen for bundle-pack price updates.
    document.addEventListener(
      'bundlePack:totalChanged',
      /** @param {CustomEvent} event */ (event) => this.#onBundleTotal(event),
      { signal }
    );

    // Wire interactions: radio change, frequency change, click-anywhere on card.
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });

    // Initial state (still works when no bundle activity has happened yet —
    // prices will simply show 0 until bundle-pack dispatches its first event).
    this.#applyVisualState();
    this.#emitChange();
    this.#renderPrices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {CustomEvent} event */
  #onBundleTotal(event) {
    const { subtotal, discountedTotal } = event.detail || {};
    this.#bundleSubtotal = Number(subtotal) || 0;
    this.#bundleDiscounted = Number(discountedTotal) || 0;
    this.#renderPrices();
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.matches('input[type="radio"][data-bundle-subscribe-radio]')) {
      this.#applyVisualState();
      this.#emitChange();
      this.#renderPrices();
    } else if (target.matches('[data-bundle-subscribe-frequency]')) {
      this.#emitChange();
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    // Make the whole card clickable, except interactive children.
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'SELECT' ||
      target.closest('[data-bundle-subscribe-frequency]')
    ) {
      return;
    }
    const card = target.closest('[data-bundle-subscribe-card]');
    if (!(card instanceof HTMLElement)) return;
    const radio = card.querySelector('input[type="radio"][data-bundle-subscribe-radio]');
    if (radio instanceof HTMLInputElement && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #currentMode() {
    const checked = this.querySelector(
      'input[type="radio"][data-bundle-subscribe-radio]:checked'
    );
    return checked instanceof HTMLInputElement ? checked.value : 'one-time';
  }

  #currentFrequency() {
    const select = this.querySelector('[data-bundle-subscribe-frequency]');
    return select instanceof HTMLSelectElement ? select.value : null;
  }

  #applyVisualState() {
    const mode = this.#currentMode();
    const isSubscribe = mode === 'subscribe';

    const subscribeCard = this.querySelector('[data-bundle-subscribe-card="subscribe"]');
    const oneTimeCard = this.querySelector('[data-bundle-subscribe-card="one-time"]');
    subscribeCard?.classList.toggle('is-active', isSubscribe);
    oneTimeCard?.classList.toggle('is-active', !isSubscribe);

    const benefits = this.querySelector('[data-bundle-subscribe-benefits]');
    if (benefits instanceof HTMLElement) {
      benefits.style.display = isSubscribe ? 'grid' : 'none';
    }

    const vipBadge = this.querySelector('[data-bundle-subscribe-vip]');
    if (vipBadge instanceof HTMLElement) {
      vipBadge.style.display = isSubscribe ? 'inline-flex' : 'none';
    }
  }

  #emitChange() {
    document.dispatchEvent(
      new CustomEvent('bundleSubscribe:change', {
        detail: {
          mode: this.#currentMode(),
          frequency: this.#currentMode() === 'subscribe' ? this.#currentFrequency() : null,
          discountPercentage: this.#discountPercentage,
        },
      })
    );
  }

  #renderPrices() {
    // One-time price = bundle's discounted total (already includes tier discount).
    // Subscribe price = one-time × (1 - discount%/100), cumulated multiplicatively.
    // Both prices are shown unconditionally — they fall back to 0,00 € when
    // nothing is selected yet and update live as the customer composes the bundle.
    const oneTimeFinal = this.querySelector('[data-bundle-subscribe-onetime-final]');
    const oneTimeOriginal = this.querySelector('[data-bundle-subscribe-onetime-original]');
    const subscribeFinal = this.querySelector('[data-bundle-subscribe-subscribe-final]');
    const subscribeOriginal = this.querySelector('[data-bundle-subscribe-subscribe-original]');

    const oneTimeCents = this.#bundleDiscounted;
    const subscribeCents = Math.round(oneTimeCents * (1 - this.#discountPercentage / 100));

    if (oneTimeFinal instanceof HTMLElement) {
      oneTimeFinal.textContent = this.#formatMoney(oneTimeCents);
    }
    if (oneTimeOriginal instanceof HTMLElement) {
      // Show the pre-tier-discount subtotal as strikethrough only if it differs.
      if (this.#bundleSubtotal > oneTimeCents) {
        oneTimeOriginal.textContent = this.#formatMoney(this.#bundleSubtotal);
        oneTimeOriginal.style.display = 'inline';
      } else {
        oneTimeOriginal.textContent = '';
        oneTimeOriginal.style.display = 'none';
      }
    }

    if (subscribeFinal instanceof HTMLElement) {
      subscribeFinal.textContent = this.#formatMoney(subscribeCents);
    }
    if (subscribeOriginal instanceof HTMLElement) {
      // The subscribe card always shows the one-time price as strikethrough
      // — that's the saving the customer makes by going subscribe.
      if (oneTimeCents > subscribeCents) {
        subscribeOriginal.textContent = this.#formatMoney(oneTimeCents);
        subscribeOriginal.style.display = 'inline';
      } else {
        subscribeOriginal.textContent = '';
        subscribeOriginal.style.display = 'none';
      }
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    const value = cents / 100;
    /** @type {Record<string, () => string>} */
    const formatters = {
      amount: () => value.toFixed(2),
      amount_no_decimals: () => Math.round(value).toString(),
      amount_with_comma_separator: () => value.toFixed(2).replace('.', ','),
      amount_no_decimals_with_comma_separator: () =>
        Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      amount_with_space_separator: () =>
        value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
      amount_no_decimals_with_space_separator: () =>
        Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
      amount_with_apostrophe_separator: () =>
        value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'"),
    };
    return this.#moneyFormat.replace(/{{\s*(\w+)\s*}}/g, (_, key) =>
      formatters[key] ? formatters[key]() : value.toFixed(2).replace('.', ',')
    );
  }
}

if (!customElements.get('bundle-subscribe')) {
  customElements.define('bundle-subscribe', BundleSubscribe);
}
